import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useGetMe,
  useListInboundMailboxes,
  getListInboundMailboxesQueryKey,
  useListInboundEmails,
  useGetInboundVerificationStats,
  getGetInboundVerificationStatsQueryKey,
  useGetGmailStatus,
  useListGmailMessages,
  useArchiveGmailMessage,
  useSetGmailMessageReadState,
  useSetThreadState,
  useDismissThreadFlags,
  getListInboundEmailsQueryKey,
  getGetGmailStatusQueryKey,
  getListGmailMessagesQueryKey,
  type ListInboundEmailsVerification,
  type ListGmailMessagesRead,
  type GmailStatus,
  type GmailMessageListItem,
} from "@workspace/api-client-react";
import { GMAIL_VIEW_VALUES, GMAIL_READ_VALUES, INBOUND_VIEW_VALUES, INBOUND_READ_VALUES, INBOUND_VERIFICATION_FILTER_VALUES } from "@/lib/enumValues";
import {
  Loader2,
  Inbox as InboxIcon,
  Mail,
  MailOpen,
  MessagesSquare,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  ShieldQuestion,
  Archive,
  MoreHorizontal,
  Pin,
  PinOff,
  Clock,
  BellRing,
  X,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { LastUpdatedIndicator } from "@/components/LastUpdatedIndicator";
import { VerifiedSenderBadge } from "@/components/VerifiedSenderBadge";
import { FailedVerificationWarning } from "@/components/FailedVerificationWarning";
import type { VerificationDetails } from "@/components/FailedVerificationWarning";
import type { VerificationStatus } from "@/components/VerifiedSenderBadge";
import { useTrustedSenderCheck } from "@/hooks/useTrustedSenderCheck";
import { useLookbackDays, LOOKBACK_OPTIONS } from "@/hooks/useLookbackDays";
import { useInboxReadFilter, useInboxViewFilter } from "@/hooks/useInboxFilters";
import { apiErrorData, apiErrorMessage } from "@/lib/apiError";
import {
  useGmailReconnect,
  GMAIL_SEND_SCOPE,
  GMAIL_MODIFY_SCOPE,
} from "@/hooks/useGmailReconnect";

// Safety-net only. Real-time updates arrive over the SSE channel
// (`/api/inbox/gmail/events`), driven by Gmail Pub/Sub push. We keep a
// 5-minute background poll as a backstop for: (a) deploys where push
// isn't configured (`GMAIL_PUBSUB_TOPIC` unset → SSE still streams
// keepalives but nothing nudges it), and (b) brief disconnects where
// the EventSource drops between Pub/Sub delivery and reconnect. The
// inbound section is unaffected — there's no Pub/Sub equivalent for
// it yet, so it relies on the poll alone.
const INBOX_REFETCH_INTERVAL_MS = 5 * 60 * 1000;
// Grace period beyond the polling interval before we flag the "Updated …"
// label as stale; mirrors the dashboard so a late refetch (slow network,
// brief tab throttling) doesn't flicker the indicator into a warning state.
const INBOX_STALE_GRACE_MS = 15_000;

// Gmail OAuth scope constants + the OAuth grant flow itself live in
// `@/hooks/useGmailReconnect` so the inline "Re-grant Gmail send
// access" button on the Gmail reply form (gmail-detail.tsx) can share
// the exact same Clerk reauthorize codepath the Inbox CTAs use.

function relTime(iso: string): string {
  const ts = new Date(iso).getTime();
  if (!Number.isFinite(ts)) return iso;
  const now = Date.now();
  const diffSec = Math.round((now - ts) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 7 * 86400) return `${Math.floor(diffSec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

// Render a future timestamp as "until Tue 9:00 AM" / "until May 12".
// Used by the "Snoozed until X" badge.
function untilLabel(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const within7d = d.getTime() - now.getTime() < 7 * 86400_000;
  const time = d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  if (sameDay) return `until ${time}`;
  if (within7d)
    return `until ${d.toLocaleDateString([], { weekday: "short" })} ${time}`;
  return `until ${d.toLocaleDateString()}`;
}

// Compute the inbound thread key (the synthetic key used by the
// thread_user_states table). Mirrors the SQL
// `coalesce(thread_id, 'msg:' || id)` expression in the inbox query.
function inboundThreadKey(item: { id: number; threadId: string | null }): string {
  return item.threadId ?? `msg:${item.id}`;
}

// Quick-pick presets for snooze/remind. We keep these client-side and
// resolve them to absolute ISO timestamps before hitting the API so
// the server stays oblivious to the user's preferred time zone.
function nextMorning9am(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d;
}
function nextMondayMorning(): Date {
  const d = new Date();
  // 0 = Sunday … 6 = Saturday. Skip ahead to next Monday (always at
  // least 1 day away so "snooze for the weekend" feels right).
  const daysUntilMonday = ((1 - d.getDay() + 7) % 7) || 7;
  d.setDate(d.getDate() + daysUntilMonday);
  d.setHours(9, 0, 0, 0);
  return d;
}
function inOneHour(): Date {
  return new Date(Date.now() + 60 * 60_000);
}
function inThreeHours(): Date {
  return new Date(Date.now() + 3 * 60 * 60_000);
}
function inOneDay(): Date {
  return new Date(Date.now() + 24 * 60 * 60_000);
}
function inOneWeek(): Date {
  return new Date(Date.now() + 7 * 24 * 60 * 60_000);
}

// `<input type="datetime-local">` returns a string in the user's local
// time zone with no offset suffix (e.g. "2026-05-02T14:30"). Parse it
// as a local Date so the API receives the exact instant the user
// picked. Returns null when the value is empty or unparseable.
function parseLocalDateTimeInput(raw: string): Date | null {
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isFinite(d.getTime()) ? d : null;
}

// Suggests a sensible default datetime for the custom picker — one
// hour from now, rounded to the next 5-minute mark, formatted for
// the `datetime-local` input.
function defaultCustomDateTimeValue(): string {
  const d = new Date(Date.now() + 60 * 60_000);
  d.setSeconds(0, 0);
  d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate(),
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Small modal used by both Snooze and Remind to capture an arbitrary
// future datetime. Validates client-side (must be in the future) so
// the user gets immediate feedback instead of round-tripping to the
// API only to bounce off the server's "must be in the future" check.
function CustomTimePickerDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: (when: Date) => void;
}) {
  const [value, setValue] = useState<string>(defaultCustomDateTimeValue);
  const [error, setError] = useState<string | null>(null);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setValue(defaultCustomDateTimeValue());
      setError(null);
    }
    props.onOpenChange(next);
  };

  const handleConfirm = () => {
    const when = parseLocalDateTimeInput(value);
    if (!when) {
      setError("Pick a valid date and time.");
      return;
    }
    if (when.getTime() <= Date.now()) {
      setError("Pick a date and time in the future.");
      return;
    }
    setError(null);
    props.onConfirm(when);
    props.onOpenChange(false);
  };

  return (
    <Dialog open={props.open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>{props.title}</DialogTitle>
          <DialogDescription>{props.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Input
            type="datetime-local"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (error) setError(null);
            }}
            data-testid="custom-time-picker-input"
            autoFocus
          />
          {error && (
            <p className="text-xs text-destructive" role="alert">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => props.onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            data-testid="custom-time-picker-confirm"
          >
            {props.confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Per-row action menu shared by the inbound and Gmail lists. Renders
// as a kebab button that opens a dropdown of pin/snooze/remind
// actions. Wraps the generated mutations with optimistic invalidation
// of all inbox queries (active/snoozed/pinned) plus the Gmail list so
// the UI catches up immediately without a refetchInterval round-trip.
//
// `onMutated` is called after every successful mutation — the parent
// uses it to surface a toast or to scroll back to the top.
function ThreadActionMenu(props: {
  kind: "inbound" | "gmail";
  threadKey: string;
  pinnedAt: string | null;
  snoozedUntil: string | null;
  reminderAt: string | null;
  onMutated?: () => void;
}) {
  const qc = useQueryClient();
  const setState = useSetThreadState();
  const dismissFlags = useDismissThreadFlags();
  const isPinned = props.pinnedAt !== null;
  const isFutureSnoozed =
    props.snoozedUntil !== null &&
    new Date(props.snoozedUntil).getTime() > Date.now();
  const hasReminder = props.reminderAt !== null;
  const [customSnoozeOpen, setCustomSnoozeOpen] = useState(false);
  const [customReminderOpen, setCustomReminderOpen] = useState(false);

  // Invalidate every inbox query (the read/view/mailbox cross-product
  // is too wide to be precise about). React Query hashes keys by
  // structure so a single predicate call is cheaper than guessing.
  const invalidate = () => {
    qc.invalidateQueries({
      predicate: (q) => {
        const k = q.queryKey?.[0];
        return (
          typeof k === "string" &&
          (k.startsWith("/api/inbox/emails") ||
            k.startsWith("/api/inbox/gmail/messages"))
        );
      },
    });
    props.onMutated?.();
  };

  const apply = (data: {
    pinned?: boolean | null;
    snoozedUntil?: string | null;
    reminderAt?: string | null;
  }) => {
    setState.mutate(
      { kind: props.kind, threadId: props.threadKey, data },
      { onSuccess: invalidate },
    );
  };

  const dismiss = () => {
    dismissFlags.mutate(
      { kind: props.kind, threadId: props.threadKey },
      { onSuccess: invalidate },
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={(e) => {
            // Stop the click from bubbling up to the row's <Link>.
            e.stopPropagation();
            e.preventDefault();
          }}
          data-testid={`thread-actions-${props.kind}-${props.threadKey}`}
          aria-label="Thread actions"
        >
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenuItem
          onSelect={() => apply({ pinned: !isPinned })}
          data-testid={`thread-action-${isPinned ? "unpin" : "pin"}`}
        >
          {isPinned ? (
            <>
              <PinOff className="w-4 h-4 mr-2" />
              Unpin
            </>
          ) : (
            <>
              <Pin className="w-4 h-4 mr-2" />
              Pin to top
            </>
          )}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Snooze
        </DropdownMenuLabel>
        <DropdownMenuItem
          onSelect={() =>
            apply({ snoozedUntil: inOneHour().toISOString() })
          }
          data-testid="thread-action-snooze-1h"
        >
          <Clock className="w-4 h-4 mr-2" />
          In 1 hour
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() =>
            apply({ snoozedUntil: inThreeHours().toISOString() })
          }
          data-testid="thread-action-snooze-3h"
        >
          <Clock className="w-4 h-4 mr-2" />
          In 3 hours
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() =>
            apply({ snoozedUntil: nextMorning9am().toISOString() })
          }
          data-testid="thread-action-snooze-tomorrow"
        >
          <Clock className="w-4 h-4 mr-2" />
          Tomorrow morning
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() =>
            apply({ snoozedUntil: nextMondayMorning().toISOString() })
          }
          data-testid="thread-action-snooze-next-week"
        >
          <Clock className="w-4 h-4 mr-2" />
          Next Monday
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => setCustomSnoozeOpen(true)}
          data-testid="thread-action-snooze-custom"
        >
          <Clock className="w-4 h-4 mr-2" />
          Custom…
        </DropdownMenuItem>
        {isFutureSnoozed && (
          <DropdownMenuItem
            onSelect={() => apply({ snoozedUntil: null })}
            data-testid="thread-action-unsnooze"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel snooze
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Remind me
        </DropdownMenuLabel>
        <DropdownMenuItem
          onSelect={() =>
            apply({ reminderAt: inOneHour().toISOString() })
          }
          data-testid="thread-action-remind-1h"
        >
          <BellRing className="w-4 h-4 mr-2" />
          In 1 hour
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() =>
            apply({ reminderAt: inThreeHours().toISOString() })
          }
          data-testid="thread-action-remind-3h"
        >
          <BellRing className="w-4 h-4 mr-2" />
          In 3 hours
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() =>
            apply({ reminderAt: inOneDay().toISOString() })
          }
          data-testid="thread-action-remind-1d"
        >
          <BellRing className="w-4 h-4 mr-2" />
          In 1 day
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() =>
            apply({ reminderAt: inOneWeek().toISOString() })
          }
          data-testid="thread-action-remind-1w"
        >
          <BellRing className="w-4 h-4 mr-2" />
          In 1 week
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => setCustomReminderOpen(true)}
          data-testid="thread-action-remind-custom"
        >
          <BellRing className="w-4 h-4 mr-2" />
          Custom…
        </DropdownMenuItem>
        {hasReminder && (
          <DropdownMenuItem
            onSelect={() => apply({ reminderAt: null })}
            data-testid="thread-action-cancel-reminder"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel reminder
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={dismiss}
          data-testid="thread-action-dismiss-flags"
        >
          <X className="w-4 h-4 mr-2" />
          Dismiss snooze/reminder badges
        </DropdownMenuItem>
      </DropdownMenuContent>
      <CustomTimePickerDialog
        open={customSnoozeOpen}
        onOpenChange={setCustomSnoozeOpen}
        title="Snooze until…"
        description="Pick a date and time to bring this thread back."
        confirmLabel="Snooze"
        onConfirm={(when) =>
          apply({ snoozedUntil: when.toISOString() })
        }
      />
      <CustomTimePickerDialog
        open={customReminderOpen}
        onOpenChange={setCustomReminderOpen}
        title="Remind me at…"
        description="We'll surface a reminder badge on this thread at that time, unless a reply arrives first."
        confirmLabel="Set reminder"
        onConfirm={(when) =>
          apply({ reminderAt: when.toISOString() })
        }
      />
    </DropdownMenu>
  );
}

// Renders a small Gmail-category pill (Primary / Promotions / Social /
// Updates / Forums) so users can tell at a glance which Gmail tab a
// row would normally land in. Pure presentation.
function GmailCategoryBadge(props: {
  category: GmailMessageListItem["category"];
}) {
  const label =
    props.category === "primary"
      ? "Primary"
      : props.category === "promotions"
        ? "Promotions"
        : props.category === "social"
          ? "Social"
          : props.category === "updates"
            ? "Updates"
            : "Forums";
  const tone =
    props.category === "primary"
      ? "border-slate-400/60 text-slate-700 dark:text-slate-300"
      : props.category === "promotions"
        ? "border-emerald-400/60 text-emerald-700 dark:text-emerald-300"
        : props.category === "social"
          ? "border-blue-400/60 text-blue-700 dark:text-blue-300"
          : props.category === "updates"
            ? "border-amber-400/60 text-amber-700 dark:text-amber-300"
            : "border-purple-400/60 text-purple-700 dark:text-purple-300";
  return (
    <Badge
      variant="outline"
      className={`shrink-0 h-5 px-1.5 text-[10px] ${tone}`}
      data-testid={`gmail-category-${props.category}`}
    >
      {label}
    </Badge>
  );
}

// Compact badge cluster for a thread row. Surfaces pin / future-snooze /
// just-unsnoozed / reminder-fired states. Pure presentation — no
// mutations live here. The action menu owns all writes.
function ThreadFlagBadges(props: {
  pinnedAt: string | null;
  snoozedUntil: string | null;
  justUnsnoozed: boolean;
  reminderFiredAt: string | null;
}) {
  const isFutureSnoozed =
    props.snoozedUntil !== null &&
    new Date(props.snoozedUntil).getTime() > Date.now();
  return (
    <>
      {props.pinnedAt && (
        <Badge
          variant="outline"
          className="shrink-0 h-5 px-1.5 text-[10px] gap-1 border-amber-400/60 text-amber-700 dark:text-amber-300"
          data-testid="thread-badge-pinned"
        >
          <Pin className="w-3 h-3" />
          Pinned
        </Badge>
      )}
      {isFutureSnoozed && props.snoozedUntil && (
        <Badge
          variant="outline"
          className="shrink-0 h-5 px-1.5 text-[10px] gap-1 border-sky-400/60 text-sky-700 dark:text-sky-300"
          data-testid="thread-badge-snoozed"
        >
          <Clock className="w-3 h-3" />
          {untilLabel(props.snoozedUntil)}
        </Badge>
      )}
      {props.justUnsnoozed && !isFutureSnoozed && (
        <Badge
          variant="secondary"
          className="shrink-0 h-5 px-1.5 text-[10px] gap-1"
          data-testid="thread-badge-just-unsnoozed"
        >
          <Clock className="w-3 h-3" />
          Just unsnoozed
        </Badge>
      )}
      {props.reminderFiredAt && (
        <Badge
          variant="default"
          className="shrink-0 h-5 px-1.5 text-[10px] gap-1"
          data-testid="thread-badge-reminder"
        >
          <BellRing className="w-3 h-3" />
          Reminder
        </Badge>
      )}
    </>
  );
}

// ── Gmail section ───────────────────────────────────────────────────────
//
// Renders above the inbound section on the Inbox page so the signed-in
// user's personal Gmail Primary inbox is visible alongside the shared
// MailCraft mailboxes. The whole section is gated on the Gmail status
// endpoint:
//
//   not_google_user      → render nothing (user signed in w/ email or
//                          another provider; Gmail isn't relevant).
//   gmail_not_connected  → "Connect Gmail" CTA. Calls
//                          user.createExternalAccount with the
//                          gmail.readonly scope.
//   gmail_scope_missing  → same shape, but we ask Clerk to reauthorize
//                          the existing Google account with the extra
//                          scope.
//   gmail_token_revoked  → same as scope_missing — token must be
//                          re-issued by walking through Google again.
//   ok                   → render the message list with read/unread
//                          filter and a 30s poll mirroring the inbound
//                          section.
function GmailSection() {
  const queryClient = useQueryClient();
  const [readFilter, setReadFilter] = useInboxReadFilter();
  const [viewFilter, setViewFilter] = useInboxViewFilter();
  const gmailMe = useGetMe().data;
  const gmailIsAdmin = gmailMe ? gmailMe.isAdmin === true : undefined;
  const {
    reconnect,
    isPending: reconnecting,
    error: reconnectError,
  } = useGmailReconnect();

  const statusQuery = useGetGmailStatus({
    query: {
      queryKey: getGetGmailStatusQueryKey(),
      // Status itself doesn't need to poll — it only changes when the
      // user reconnects (which we invalidate manually) or signs out.
      // But a slow refetch lets the section recover automatically if
      // the token is revoked from Google's side mid-session.
      refetchInterval: 5 * 60 * 1000,
    },
  });

  const status: GmailStatus | undefined = statusQuery.data;
  const isConnected = status?.connected === true;

  const messagesQuery = useListGmailMessages(
    { read: readFilter, view: viewFilter, limit: 50 },
    {
      query: {
        queryKey: getListGmailMessagesQueryKey({
          read: readFilter,
          view: viewFilter,
          limit: 50,
        }),
        // Only fire the network call once we know Gmail is connected.
        // Otherwise the API would 412 and we'd flash an error in the
        // section even though the CTA below already explains the gap.
        enabled: isConnected,
        refetchInterval: INBOX_REFETCH_INTERVAL_MS,
      },
    },
  );

  // Real-time refresh wiring. While Gmail is connected we:
  //   1. POST /inbox/gmail/watch once to register (or refresh) the
  //      Pub/Sub watch on the user's mailbox. Idempotent — calling on
  //      every connect is the cheapest way to handle expiry too.
  //   2. Open an SSE stream to /inbox/gmail/events. Each `refresh`
  //      event from the server invalidates the Gmail list query keys
  //      so React Query refetches immediately. The stream sends
  //      keepalive comments every ~25s; the browser auto-reconnects
  //      on drop.
  // Both are cleaned up when GmailSection unmounts or Gmail
  // disconnects. We deliberately don't surface watch/SSE failures in
  // the UI — the 5-minute safety-net poll covers any silent gap.
  const watchAttemptedRef = useRef(false);
  useEffect(() => {
    if (!isConnected) {
      watchAttemptedRef.current = false;
      return;
    }

    if (!watchAttemptedRef.current) {
      watchAttemptedRef.current = true;
      void fetch("/api/inbox/gmail/watch", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: "{}",
      }).catch((err) => {
        // Best-effort. The poll keeps things moving if push isn't up.
        console.warn("[gmail] watch register failed", err);
      });
    }

    if (typeof window === "undefined" || typeof EventSource === "undefined") {
      return;
    }
    const es = new EventSource("/api/inbox/gmail/events", {
      withCredentials: true,
    });
    const handleRefresh = () => {
      // Invalidate every variant of the Gmail list query (any
      // read/view/limit combo currently in the cache) plus the status
      // query so a token-revoked event surfaces immediately.
      // The orval-generated key is `[path, params]`; matching by the
      // path prefix invalidates every cached read/view/limit variant.
      queryClient.invalidateQueries({
        queryKey: ["/api/inbox/gmail/messages"],
      });
      queryClient.invalidateQueries({ queryKey: getGetGmailStatusQueryKey() });
    };
    es.addEventListener("refresh", handleRefresh);
    es.addEventListener("error", () => {
      // EventSource auto-reconnects with backoff. Nothing to do here
      // beyond letting it run; the safety-net poll covers any extended
      // outage.
    });
    return () => {
      es.removeEventListener("refresh", handleRefresh);
      es.close();
    };
  }, [isConnected, queryClient]);

  // Loading: the very first status request hasn't resolved yet. Render
  // a placeholder card so the layout doesn't pop in.
  if (statusQuery.isLoading) {
    return (
      <div
        className="border-b border-border bg-card"
        data-testid="gmail-section-loading"
      >
        <div className="px-6 py-4 flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Checking Gmail connection…
        </div>
      </div>
    );
  }

  // Hide entirely for non-Google sign-ins. Per the task: this section
  // is opt-in based on the auth provider, not a generic add-on every
  // user can wire up.
  if (status?.reason === "not_google_user") {
    return null;
  }

  // Status fetch failed for some non-412 reason (network, server bug).
  // Don't pretend Gmail is unavailable — just stay quiet. The user can
  // still use the inbound section below, and the next poll will retry.
  if (!status) {
    return null;
  }

  const reasonLabel: Record<string, string> = {
    gmail_not_connected: "Connect your Gmail to see your inbox here.",
    gmail_scope_missing:
      "MailCraft needs read-only Gmail access. Reauthorize to continue.",
    gmail_token_revoked:
      "Your Gmail connection was revoked or expired. Reconnect to continue.",
  };
  const isReconnect =
    status.reason === "gmail_scope_missing" ||
    status.reason === "gmail_token_revoked";

  // Walk the user through Google's consent screen. The shared
  // `reconnect` helper handles the createExternalAccount /
  // reauthorize fork, navigates to Google, and reports any error
  // through the hook's `reconnectError` field.
  const handleConnect = () => {
    void reconnect();
  };

  // Disconnected variants: render the explanatory CTA card.
  if (!isConnected) {
    const helpText = status.reason
      ? (reasonLabel[status.reason] ?? "Gmail is not available.")
      : "Gmail is not available.";
    return (
      <div
        className="border-b border-border bg-card"
        data-testid="gmail-section-disconnected"
      >
        <div className="px-6 py-4 flex items-start gap-3">
          <div className="mt-0.5 shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Mail className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm flex items-center gap-2">
              Gmail
              <Badge variant="outline" className="text-[10px] uppercase">
                Beta
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{helpText}</p>
            {status.gmailAddress && (
              <p className="text-xs text-muted-foreground mt-0.5 font-mono">
                {status.gmailAddress}
              </p>
            )}
            {reconnectError && (
              <p
                className="text-xs text-destructive mt-1"
                data-testid="gmail-connect-error"
              >
                {reconnectError}
              </p>
            )}
          </div>
          <Button
            size="sm"
            onClick={handleConnect}
            disabled={reconnecting}
            data-testid="gmail-connect-button"
          >
            {reconnecting ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : null}
            {isReconnect ? "Reconnect Gmail" : "Connect Gmail"}
          </Button>
        </div>
      </div>
    );
  }

  // Connected: render the listing. If the messages query bounces with
  // a 412 (the token was revoked between polls) we fall back to the
  // disconnected card by inspecting the error payload.
  const queryErr = messagesQuery.error;
  const queryErrData = apiErrorData<{ reason?: string; gmailAddress?: string | null }>(queryErr);
  const tokenJustRevoked =
    queryErrData?.reason === "gmail_token_revoked" ||
    queryErrData?.reason === "gmail_scope_missing";
  if (tokenJustRevoked) {
    return (
      <div
        className="border-b border-border bg-card"
        data-testid="gmail-section-disconnected"
      >
        <div className="px-6 py-4 flex items-start gap-3">
          <div className="mt-0.5 shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Mail className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm">Gmail</div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Your Gmail connection was revoked or expired. Reconnect to
              continue.
            </p>
          </div>
          <Button
            size="sm"
            onClick={handleConnect}
            disabled={reconnecting}
            data-testid="gmail-connect-button"
          >
            {reconnecting ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : null}
            Reconnect Gmail
          </Button>
        </div>
      </div>
    );
  }

  const messages: GmailMessageListItem[] = messagesQuery.data?.messages ?? [];
  // Treat empty strings as missing so a transient empty value from one
  // source doesn't override a populated value from the other. The
  // server returns `null` for "address unknown" but we defensively
  // handle "" too in case an older build is still in flight.
  const messagesAddress = messagesQuery.data?.gmailAddress?.trim() || null;
  const statusAddress =
    typeof status.gmailAddress === "string" && status.gmailAddress.trim()
      ? status.gmailAddress.trim()
      : null;
  const gmailAddress = messagesAddress ?? statusAddress ?? "";
  const unreadCount: number | null =
    typeof messagesQuery.data?.unreadCount === "number"
      ? messagesQuery.data.unreadCount
      : null;

  // Did the user grant the new write scopes? Drives the row actions
  // (mark/archive) and the inline reconnect banner shown to legacy
  // readonly-only users.
  const grantedScopes = status.scopes ?? [];
  const canSend = grantedScopes.includes(GMAIL_SEND_SCOPE);
  const canModify = grantedScopes.includes(GMAIL_MODIFY_SCOPE);
  const needsReconnect = !canSend || !canModify;

  return (
    <div className="border-b border-border bg-card" data-testid="gmail-section">
      <div className="px-6 pt-4 pb-3 flex items-center gap-3 flex-wrap">
        <div className="shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
          <Mail className="w-4 h-4 text-primary" />
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-sm flex items-center gap-2">
            Gmail
            {unreadCount !== null && unreadCount > 0 && (
              <Badge
                variant="destructive"
                className="text-[10px] px-1.5 py-0 min-w-[1.25rem] justify-center"
                title={`${unreadCount} unread Gmail messages`}
                data-testid="gmail-unread-badge"
              >
                {unreadCount}
              </Badge>
            )}
            {needsReconnect ? (
              <Badge variant="secondary" className="text-[10px]">
                Read-only
              </Badge>
            ) : (
              <Badge
                variant="default"
                className="text-[10px]"
                data-testid="gmail-write-enabled-badge"
              >
                Reply &amp; Manage
              </Badge>
            )}
          </div>
          <p
            className="text-xs text-muted-foreground font-mono truncate"
            data-testid="gmail-address"
          >
            {gmailAddress}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <LastUpdatedIndicator
            updatedAt={messagesQuery.dataUpdatedAt}
            isFetching={messagesQuery.isFetching}
            staleAfterMs={INBOX_REFETCH_INTERVAL_MS + INBOX_STALE_GRACE_MS}
            testId="gmail-last-updated"
            staleSuffix=" — new messages may be missing"
          />
          <div className="flex gap-1 rounded-md border border-border p-0.5">
            {GMAIL_VIEW_VALUES.map((opt) => (
              <button
                key={opt}
                onClick={() => setViewFilter(opt)}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  viewFilter === opt
                    ? "bg-secondary text-secondary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`gmail-view-${opt}`}
              >
                {opt[0].toUpperCase() + opt.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex gap-1 rounded-md border border-border p-0.5">
            {GMAIL_READ_VALUES.map((opt) => (
              <button
                key={opt}
                onClick={() => setReadFilter(opt)}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  readFilter === opt
                    ? "bg-secondary text-secondary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`gmail-read-${opt}`}
              >
                {opt[0].toUpperCase() + opt.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>
      {needsReconnect && (
        <div
          className="mx-6 mb-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 flex items-start gap-2"
          data-testid="gmail-reconnect-banner"
        >
          <ShieldAlert className="w-4 h-4 text-primary mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0 text-xs">
            <div className="font-medium text-foreground">
              Reconnect Gmail to enable replies and inbox actions
            </div>
            <p className="text-muted-foreground mt-0.5">
              Your existing connection is read-only. Re-grant access to
              reply, mark messages, archive, and manage labels from
              MailCraft.
            </p>
            {reconnectError && (
              <p
                className="text-destructive mt-1"
                data-testid="gmail-reconnect-error"
              >
                {reconnectError}
              </p>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleConnect}
            disabled={reconnecting}
            data-testid="gmail-reconnect-button"
          >
            {reconnecting ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : null}
            Reconnect
          </Button>
        </div>
      )}
      <div className="max-h-[40vh] overflow-auto">
        {messagesQuery.isLoading ? (
          <div className="p-6 flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : messagesQuery.isError && !tokenJustRevoked ? (
          <div className="px-6 py-6 text-center text-sm text-muted-foreground">
            Couldn't load Gmail messages. Try again in a moment.
          </div>
        ) : messages.length === 0 ? (
          <div className="px-6 py-6 text-center text-sm text-muted-foreground">
            {viewFilter === "snoozed"
              ? "No snoozed Gmail threads."
              : viewFilter === "pinned"
                ? "No pinned Gmail threads."
                : "No Gmail messages match this filter."}
          </div>
        ) : (
          <ul
            className="divide-y divide-border"
            data-testid="gmail-message-list"
          >
            {messages.map((m) => (
              <GmailListRow
                key={m.id}
                message={m}
                canModify={canModify}
                readFilter={readFilter}
                isAdmin={gmailIsAdmin}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── Gmail row ──────────────────────────────────────────────────────────
//
// Rendered for every message in the connected Gmail list. Wraps the row
// link in a relative container so the hover-only action buttons can
// stop event propagation (otherwise tapping Archive would also navigate
// to the detail page). Mutations are optimistic: we patch the cached
// message list immediately, then roll back on error.
function GmailListRow({
  message,
  canModify,
  readFilter,
  isAdmin,
}: {
  message: GmailMessageListItem;
  canModify: boolean;
  readFilter: ListGmailMessagesRead;
  isAdmin?: boolean;
}) {
  const queryClient = useQueryClient();
  const listKey = getListGmailMessagesQueryKey({
    read: readFilter,
    limit: 50,
  });

  type ListSnapshot = { gmailAddress: string; messages: GmailMessageListItem[] };

  const patchList = (
    updater: (prev: GmailMessageListItem[]) => GmailMessageListItem[],
  ): ListSnapshot | undefined => {
    const prev = queryClient.getQueryData<ListSnapshot>(listKey);
    if (!prev) return undefined;
    queryClient.setQueryData<ListSnapshot>(listKey, {
      ...prev,
      messages: updater(prev.messages),
    });
    return prev;
  };

  const setReadStateMutation = useSetGmailMessageReadState();
  const archiveMutation = useArchiveGmailMessage();

  const onMarkToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const becomingRead = message.isUnread;
    const snap = patchList((rows) =>
      rows.map((r) =>
        r.id === message.id ? { ...r, isUnread: !becomingRead } : r,
      ),
    );
    try {
      await setReadStateMutation.mutateAsync({
        id: message.id,
        data: { read: becomingRead },
      });
      // Status-impacting changes can desync filters; refetch in
      // background so the next interaction is consistent.
      queryClient.invalidateQueries({ queryKey: listKey });
    } catch (err) {
      if (snap) queryClient.setQueryData(listKey, snap);
      const data = apiErrorData<{ reason?: string }>(err);
      if (data?.reason === "gmail_scope_missing") {
        toast.error(
          "Reconnect Gmail to enable inbox actions (modify scope missing).",
        );
      } else {
        toast.error(apiErrorMessage(err, "Couldn't update read state"));
      }
    }
  };

  const onArchive = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Optimistically remove the row from the visible list — Gmail
    // archive removes INBOX, and this view is the inbox.
    const snap = patchList((rows) => rows.filter((r) => r.id !== message.id));
    try {
      await archiveMutation.mutateAsync({ id: message.id });
      toast.success("Archived");
      queryClient.invalidateQueries({ queryKey: listKey });
    } catch (err) {
      if (snap) queryClient.setQueryData(listKey, snap);
      const data = apiErrorData<{ reason?: string }>(err);
      if (data?.reason === "gmail_scope_missing") {
        toast.error(
          "Reconnect Gmail to enable inbox actions (modify scope missing).",
        );
      } else {
        toast.error(apiErrorMessage(err, "Couldn't archive message"));
      }
    }
  };

  return (
    <li className="relative group">
      <Link
        href={`/app/inbox/gmail/${message.id}`}
        className={`flex items-start gap-3 px-6 py-3 hover:bg-muted/40 transition-colors ${
          message.isUnread ? "bg-card" : ""
        }`}
        data-testid={`gmail-row-${message.id}`}
      >
        <div className="pt-1 shrink-0">
          {message.isUnread ? (
            <Mail className="w-4 h-4 text-primary" />
          ) : (
            <MailOpen className="w-4 h-4 text-muted-foreground" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span
              className={`text-sm truncate ${
                message.isUnread
                  ? "font-semibold text-foreground"
                  : "text-muted-foreground"
              }`}
            >
              {message.fromName || message.fromAddress}
            </span>
            <Badge
              variant="outline"
              className="shrink-0 h-5 px-1.5 text-[10px]"
            >
              Gmail
            </Badge>
            <VerifiedSenderBadge
              verificationStatus={message.verificationStatus ?? null}
              testIdSuffix={`-gmail-row-${message.id}`}
            />
            <GmailFailedVerification
              fromAddress={message.fromAddress}
              verificationStatus={message.verificationStatus ?? null}
              verificationDetails={message.verificationDetails ?? null}
              isAdmin={isAdmin}
              testIdSuffix={`-gmail-row-${message.id}`}
            />
            <GmailCategoryBadge category={message.category} />
            <ThreadFlagBadges
              pinnedAt={message.pinnedAt ?? null}
              snoozedUntil={message.snoozedUntil ?? null}
              justUnsnoozed={message.justUnsnoozed ?? false}
              reminderFiredAt={message.reminderFiredAt ?? null}
            />
            <span className="ml-auto text-[11px] text-muted-foreground shrink-0">
              {relTime(message.receivedAt)}
            </span>
          </div>
          <div
            className={`text-sm truncate mt-0.5 ${
              message.isUnread ? "font-medium" : "text-muted-foreground"
            }`}
          >
            {message.subject || "(no subject)"}
          </div>
          {message.snippet && (
            <div className="text-xs text-muted-foreground truncate mt-0.5">
              {message.snippet}
            </div>
          )}
        </div>
      </Link>
      {canModify && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-1 bg-card/95 backdrop-blur rounded-md border border-border px-1.5 py-1 shadow-sm">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onMarkToggle}
            disabled={setReadStateMutation.isPending}
            title={message.isUnread ? "Mark as read" : "Mark as unread"}
            data-testid={`gmail-row-mark-${message.id}`}
          >
            {message.isUnread ? (
              <MailOpen className="w-3.5 h-3.5" />
            ) : (
              <Mail className="w-3.5 h-3.5" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onArchive}
            disabled={archiveMutation.isPending}
            title="Archive"
            data-testid={`gmail-row-archive-${message.id}`}
          >
            <Archive className="w-3.5 h-3.5" />
          </Button>
          <ThreadActionMenu
            kind="gmail"
            threadKey={message.threadId}
            pinnedAt={message.pinnedAt ?? null}
            snoozedUntil={message.snoozedUntil ?? null}
            reminderAt={message.reminderAt ?? null}
          />
        </div>
      )}
    </li>
  );
}

function GmailFailedVerification({
  fromAddress,
  verificationStatus,
  verificationDetails,
  isAdmin,
  testIdSuffix,
}: {
  fromAddress: string;
  verificationStatus: VerificationStatus | null;
  verificationDetails: VerificationDetails | null;
  isAdmin?: boolean;
  testIdSuffix: string;
}) {
  const { isTrusted } = useTrustedSenderCheck(fromAddress, verificationStatus);
  if (isTrusted) return null;
  return (
    <FailedVerificationWarning
      verificationStatus={verificationStatus}
      verificationDetails={verificationDetails}
      senderDomain={fromAddress}
      isAdmin={isAdmin}
      testIdSuffix={testIdSuffix}
    />
  );
}

function InboundFailedVerification({
  fromAddress,
  verificationStatus,
  verificationDetails,
  senderDomain,
  isAdmin,
  testIdSuffix,
}: {
  fromAddress: string;
  verificationStatus: VerificationStatus | null;
  verificationDetails: VerificationDetails | null;
  senderDomain?: string | null;
  isAdmin?: boolean;
  testIdSuffix: string;
}) {
  const { isTrusted } = useTrustedSenderCheck(fromAddress, verificationStatus);
  if (isTrusted) return null;
  return (
    <FailedVerificationWarning
      verificationStatus={verificationStatus}
      verificationDetails={verificationDetails}
      senderDomain={senderDomain}
      isAdmin={isAdmin}
      testIdSuffix={testIdSuffix}
    />
  );
}

const VERIFICATION_LABELS: Record<string, { label: string; color: string; icon: typeof ShieldCheck }> = {
  bimi: { label: "BIMI", color: "text-emerald-600", icon: ShieldCheck },
  dmarc: { label: "DMARC", color: "text-blue-600", icon: ShieldCheck },
  unverified: { label: "Unverified", color: "text-muted-foreground", icon: ShieldQuestion },
  fail: { label: "Failed", color: "text-destructive", icon: ShieldX },
};

function VerificationStatsPanel({
  mailbox,
  isAdmin,
  onFilterByStatus,
  activeFilter,
}: {
  mailbox: string;
  isAdmin: boolean;
  onFilterByStatus: (status: ListInboundEmailsVerification | undefined) => void;
  activeFilter: ListInboundEmailsVerification | undefined;
}) {
  const [expanded, setExpanded] = useState(false);
  const [lookbackDays, setLookbackDays] = useLookbackDays();
  const statsQuery = useGetInboundVerificationStats(
    { ...(mailbox ? { mailbox } : {}), days: lookbackDays },
    {
      query: {
        queryKey: getGetInboundVerificationStatsQueryKey({
          ...(mailbox ? { mailbox } : {}),
          days: lookbackDays,
        }),
        enabled: isAdmin,
        refetchInterval: INBOX_REFETCH_INTERVAL_MS,
      },
    },
  );

  const stats = statsQuery.data;
  if (!stats || stats.total === 0) return null;

  const failCount = stats.breakdown.fail + stats.breakdown.unverified;
  const passCount = stats.breakdown.bimi + stats.breakdown.dmarc;
  const failPct = Math.round((failCount / stats.total) * 100);

  return (
    <div className="mx-6 mt-3 mb-1 rounded-lg border border-border bg-card p-3" data-testid="verification-stats-panel">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between text-left"
        data-testid="verification-stats-toggle"
      >
        <div className="flex items-center gap-2 text-sm font-medium">
          <ShieldAlert className="w-4 h-4 text-muted-foreground" />
          Authentication (last {stats.days}d)
          {failCount > 0 && (
            <Badge variant="outline" className="text-[10px] text-destructive border-destructive/30">
              {failPct}% unauthenticated
            </Badge>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>

      {expanded && (
        <div className="mt-3 space-y-2" data-testid="verification-stats-detail">
          <div className="flex items-center gap-1 rounded-md bg-muted p-0.5 w-fit" data-testid="lookback-selector">
            {LOOKBACK_OPTIONS.map((d) => (
              <button
                key={d}
                onClick={() => setLookbackDays(d)}
                className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                  lookbackDays === d
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`lookback-${d}d`}
              >
                {d}d
              </button>
            ))}
          </div>
          <div className="flex h-2 rounded-full overflow-hidden bg-muted">
            {INBOUND_VERIFICATION_FILTER_VALUES.map((key) => {
              const count = stats.breakdown[key];
              if (count === 0) return null;
              const pct = (count / stats.total) * 100;
              const colors: Record<string, string> = {
                bimi: "bg-emerald-500",
                dmarc: "bg-blue-500",
                unverified: "bg-gray-400",
                fail: "bg-destructive",
              };
              return (
                <div
                  key={key}
                  className={`${colors[key]} transition-all`}
                  style={{ width: `${pct}%` }}
                  title={`${VERIFICATION_LABELS[key].label}: ${count} (${Math.round(pct)}%)`}
                />
              );
            })}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            {INBOUND_VERIFICATION_FILTER_VALUES.map((key) => {
              const info = VERIFICATION_LABELS[key];
              const count = stats.breakdown[key];
              const Icon = info.icon;
              const isActive = activeFilter === key;
              return (
                <button
                  key={key}
                  onClick={() => onFilterByStatus(isActive ? undefined : key)}
                  className={`flex items-center gap-1.5 rounded-md px-2 py-1.5 transition-colors ${
                    isActive
                      ? "bg-secondary ring-1 ring-primary/30"
                      : "hover:bg-muted/60"
                  }`}
                  data-testid={`verification-filter-${key}`}
                >
                  <Icon className={`w-3.5 h-3.5 ${info.color}`} />
                  <span className={info.color}>{info.label}</span>
                  <span className="ml-auto font-mono tabular-nums">{count}</span>
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground">
            {stats.total} message{stats.total !== 1 ? "s" : ""} total — {passCount} authenticated, {failCount} unauthenticated
          </p>
        </div>
      )}
    </div>
  );
}

export default function Inbox() {
  const [mailboxFilter, setMailboxFilter] = useState<string>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("mailbox") ?? "";
  });
  const [readFilter, setReadFilter] = useInboxReadFilter();
  const [viewFilter, setViewFilter] = useInboxViewFilter();
  const [verificationFilter, setVerificationFilter] = useState<ListInboundEmailsVerification | undefined>(undefined);
  const [, navigate] = useLocation();

  const meQuery = useGetMe();
  const me = meQuery.data;

  const mailboxesQuery = useListInboundMailboxes(undefined, {
    query: {
      enabled: me?.isAdmin === true,
      queryKey: getListInboundMailboxesQueryKey(),
    },
  });
  const emailsQuery = useListInboundEmails(
    {
      ...(mailboxFilter ? { mailbox: mailboxFilter } : {}),
      ...(verificationFilter ? { verification: verificationFilter } : {}),
      read: readFilter,
      view: viewFilter,
      limit: 100,
    },
    {
      query: {
        queryKey: getListInboundEmailsQueryKey({
          ...(mailboxFilter ? { mailbox: mailboxFilter } : {}),
          ...(verificationFilter ? { verification: verificationFilter } : {}),
          read: readFilter,
          view: viewFilter,
          limit: 100,
        }),
        refetchInterval: INBOX_REFETCH_INTERVAL_MS,
        enabled: me?.isAdmin === true,
      },
    },
  );

  if (meQuery.isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!me?.isAdmin) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 text-muted-foreground p-12">
        <ShieldAlert className="w-12 h-12 opacity-40" />
        <p className="font-medium text-base">Access restricted</p>
        <p className="text-sm text-center max-w-xs">
          The shared inbox is only accessible to workspace administrators.
        </p>
        <Button variant="outline" onClick={() => navigate("/app")}>
          Go to dashboard
        </Button>
      </div>
    );
  }

  if (mailboxesQuery.isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const mailboxes = mailboxesQuery.data?.mailboxes ?? [];
  const totalUnread = mailboxes.reduce((acc, m) => acc + (m.unreadCount ?? 0), 0);
  const emails = emailsQuery.data ?? [];

  return (
    <div className="flex-1 flex flex-col h-screen overflow-hidden bg-background">
      <div className="h-16 px-6 border-b border-border flex items-center justify-between shrink-0 bg-card z-10">
        <div className="flex flex-col gap-0.5">
          <h1 className="font-bold text-lg flex items-center gap-2">
            <InboxIcon className="w-5 h-5 text-primary" />
            Inbox
            {totalUnread > 0 && (
              <Badge variant="default" className="ml-2">
                {totalUnread} unread
              </Badge>
            )}
          </h1>
          <LastUpdatedIndicator
            updatedAt={emailsQuery.dataUpdatedAt}
            isFetching={emailsQuery.isFetching}
            staleAfterMs={INBOX_REFETCH_INTERVAL_MS + INBOX_STALE_GRACE_MS}
            testId="inbox-last-updated"
            staleSuffix=" — new messages may be missing"
          />
        </div>
        <div className="text-xs text-muted-foreground">
          Inbound domain:{" "}
          <span className="font-mono">
            {mailboxesQuery.data?.domain ?? "—"}
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <GmailSection />

        <VerificationStatsPanel
          mailbox={mailboxFilter}
          isAdmin={me?.isAdmin === true}
          onFilterByStatus={setVerificationFilter}
          activeFilter={verificationFilter}
        />

        {verificationFilter && (
          <div className="mx-6 mt-2 mb-1 flex items-center gap-2 text-xs text-muted-foreground" data-testid="verification-active-filter">
            Showing only: <Badge variant="secondary" className="text-[10px] gap-1">
              {VERIFICATION_LABELS[verificationFilter]?.label ?? verificationFilter}
            </Badge>
            <button
              onClick={() => setVerificationFilter(undefined)}
              className="underline hover:text-foreground transition-colors"
              data-testid="verification-clear-filter"
            >
              Clear filter
            </button>
          </div>
        )}

        <div className="px-6 py-3 border-b border-border bg-card flex flex-wrap items-center gap-2 shrink-0">
          <Button
            variant={mailboxFilter === "" ? "default" : "outline"}
            size="sm"
            onClick={() => setMailboxFilter("")}
            data-testid="inbox-filter-all"
          >
            All mailboxes
            {totalUnread > 0 && (
              <Badge variant="secondary" className="ml-2">
                {totalUnread}
              </Badge>
            )}
          </Button>
          {mailboxes.map((m) => (
            <Button
              key={m.id}
              variant={mailboxFilter === m.address ? "default" : "outline"}
              size="sm"
              onClick={() => setMailboxFilter(m.address)}
              data-testid={`inbox-filter-${m.address}`}
            >
              <span className="font-mono">{m.address}</span>
              {m.unreadCount > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {m.unreadCount}
                </Badge>
              )}
              {m.status === "pending" && (
                <Badge variant="outline" className="ml-2 text-[10px]">
                  pending
                </Badge>
              )}
            </Button>
          ))}
          <div className="flex-1" />
          <div className="flex gap-1 rounded-md border border-border p-0.5">
            {INBOUND_VIEW_VALUES.map((opt) => (
              <button
                key={opt}
                onClick={() => setViewFilter(opt)}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  viewFilter === opt
                    ? "bg-secondary text-secondary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`inbox-view-${opt}`}
              >
                {opt[0].toUpperCase() + opt.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex gap-1 rounded-md border border-border p-0.5">
            {INBOUND_READ_VALUES.map((opt) => (
              <button
                key={opt}
                onClick={() => setReadFilter(opt)}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  readFilter === opt
                    ? "bg-secondary text-secondary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`inbox-read-${opt}`}
              >
                {opt[0].toUpperCase() + opt.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {mailboxes.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <InboxIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No mailboxes configured yet.</p>
            <p className="text-xs mt-1">
              Set <code className="font-mono">MAILCRAFT_INBOUND_DOMAIN</code> and
              restart the server, or visit Brand Settings for setup
              instructions.
            </p>
          </div>
        ) : emailsQuery.isLoading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : emails.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <MailOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">
              {viewFilter === "snoozed"
                ? "No snoozed threads."
                : viewFilter === "pinned"
                  ? "No pinned threads."
                  : "Nothing to see here yet."}
            </p>
            <p className="text-xs mt-1">
              {viewFilter === "active"
                ? "Inbound messages addressed to your mailboxes will appear here."
                : viewFilter === "snoozed"
                  ? "Use the Snooze action on any thread to hide it until later."
                  : "Use the Pin action on any thread to keep it at the top."}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {emails.map((e) => {
              // A row is a *thread*; "unread" = at least one message
              // in that thread is unread, not necessarily the latest.
              const threadUnread = e.threadUnreadCount > 0;
              const isMultiMessage = e.threadCount > 1;
              const threadKey = inboundThreadKey(e);
              return (
                <li key={e.id}>
                  <div
                    className={`flex items-start gap-3 px-6 py-3.5 hover:bg-muted/40 transition-colors ${
                      threadUnread ? "bg-card" : ""
                    } ${e.pinnedAt ? "bg-amber-50/30 dark:bg-amber-950/10" : ""}`}
                    data-testid={`inbox-row-${e.id}`}
                  >
                    <Link
                      href={`/app/inbox/${e.id}`}
                      className="flex items-start gap-3 flex-1 min-w-0"
                    >
                      <div className="pt-1 shrink-0">
                        {threadUnread ? (
                          <Mail className="w-4 h-4 text-primary" />
                        ) : (
                          <MailOpen className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span
                            className={`text-sm truncate ${
                              threadUnread
                                ? "font-semibold text-foreground"
                                : "text-muted-foreground"
                            }`}
                          >
                            {e.fromName || e.fromAddress}
                          </span>
                          <VerifiedSenderBadge
                            verificationStatus={e.verificationStatus ?? null}
                            testIdSuffix={`-inbox-row-${e.id}`}
                          />
                          <InboundFailedVerification
                            fromAddress={e.fromAddress}
                            verificationStatus={e.verificationStatus ?? null}
                            verificationDetails={e.verificationDetails ?? null}
                            senderDomain={e.fromAddress}
                            isAdmin={me ? me.isAdmin === true : undefined}
                            testIdSuffix={`-inbox-row-${e.id}`}
                          />
                          {isMultiMessage && (
                            <Badge
                              variant="secondary"
                              className="shrink-0 h-5 px-1.5 text-[10px] gap-1"
                              data-testid={`inbox-thread-count-${e.id}`}
                            >
                              <MessagesSquare className="w-3 h-3" />
                              {e.threadCount}
                            </Badge>
                          )}
                          {threadUnread && e.threadUnreadCount > 1 && (
                            <Badge
                              variant="default"
                              className="shrink-0 h-5 px-1.5 text-[10px]"
                              data-testid={`inbox-thread-unread-${e.id}`}
                            >
                              {e.threadUnreadCount} new
                            </Badge>
                          )}
                          <ThreadFlagBadges
                            pinnedAt={e.pinnedAt ?? null}
                            snoozedUntil={e.snoozedUntil ?? null}
                            justUnsnoozed={e.justUnsnoozed ?? false}
                            reminderFiredAt={e.reminderFiredAt ?? null}
                          />
                          <span className="text-[11px] text-muted-foreground font-mono shrink-0">
                            → {e.mailboxAddress}
                          </span>
                          <span className="ml-auto text-[11px] text-muted-foreground shrink-0">
                            {relTime(e.receivedAt)}
                          </span>
                        </div>
                        <div
                          className={`text-sm truncate mt-0.5 ${
                            threadUnread
                              ? "font-medium"
                              : "text-muted-foreground"
                          }`}
                        >
                          {e.subject || "(no subject)"}
                        </div>
                        {e.snippet && (
                          <div className="text-xs text-muted-foreground truncate mt-0.5">
                            {e.snippet}
                          </div>
                        )}
                      </div>
                    </Link>
                    <ThreadActionMenu
                      kind="inbound"
                      threadKey={threadKey}
                      pinnedAt={e.pinnedAt ?? null}
                      snoozedUntil={e.snoozedUntil ?? null}
                      reminderAt={e.reminderAt ?? null}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
